// AntsNest Talk API 서비스
const API_BASE = 'https://talk.antsnest.co.kr/api'
const AUTH_BASE = 'https://talk.antsnest.co.kr'

class AntsTalkService {
  constructor() {
    this.token = localStorage.getItem('antstalk_token')
  }

  // API 요청 헬퍼
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
        ...options.headers
      },
      ...options
    }

    try {
      const response = await fetch(url, config)
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.message || '요청이 실패했습니다.')
      }
      
      return data
    } catch (error) {
      console.error('AntsTalk API Error:', error)
      throw error
    }
  }

  // 로그인
  async login(user_account, user_pwd) {
    try {
      // 로그인은 /auth/login (api prefix 없음!)
      const response = await fetch(`${AUTH_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_account, user_pwd })
      })
      const data = await response.json()
      
      if (data.token) {
        this.token = data.token
        localStorage.setItem('antstalk_token', data.token)
        return { success: true, user: data.user }
      }
      
      return { success: false, message: '로그인 실패' }
    } catch (error) {
      return { success: false, message: error.message }
    }
  }

  // 로그아웃
  logout() {
    this.token = null
    localStorage.removeItem('antstalk_token')
  }

  // 토큰 확인
  isAuthenticated() {
    return !!this.token
  }

  // SSO 자동 로그인 (Story 세션 쿠키 사용)
  async loginWithSSO(username) {
    try {
      // SSO 세션 쿠키로 토큰 발급 요청
      const response = await fetch(`${AUTH_BASE}/auth/sso-verify`, {
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.token) {
          this.token = data.token
          localStorage.setItem('antstalk_token', data.token)
          return { success: true, user: data.user }
        }
      }
      
      return { success: false, message: 'SSO 인증 실패' }
    } catch (error) {
      console.error('SSO verify error:', error)
      return { success: false, message: error.message }
    }
  }

  // 채팅방 목록 가져오기
  async getRooms() {
    try {
      const data = await this.request('/chat/rooms')
      return data.rooms || []
    } catch (error) {
      console.error('Failed to get rooms:', error)
      return []
    }
  }

  // 특정 채팅방의 메시지 가져오기
  async getMessages(roomId, limit = 50) {
    try {
      const data = await this.request(`/chat/messages/${roomId}?limit=${limit}`)
      return data.messages || []
    } catch (error) {
      console.error('Failed to get messages:', error)
      return []
    }
  }

  // 메시지 전송
  async sendMessage(roomId, text) {
    try {
      const data = await this.request(`/chat/messages/${roomId}`, {
        method: 'POST',
        body: JSON.stringify({ text })
      })
      return { success: true, message: data.message }
    } catch (error) {
      console.error('Failed to send message:', error)
      return { success: false, message: error.message }
    }
  }

  // 채팅방 참여자 목록 가져오기
  async getParticipants(roomId) {
    try {
      const data = await this.request(`/chat/participants/${roomId}`)
      return data.participants || []
    } catch (error) {
      console.error('Failed to get participants:', error)
      return []
    }
  }

  // 새 메시지가 있는지 확인 (폴링용)
  async checkNewMessages(roomId, lastMessageId) {
    try {
      const messages = await this.getMessages(roomId, 10)
      const newMessages = messages.filter(msg => 
        lastMessageId ? msg.id > lastMessageId : false
      )
      return newMessages
    } catch (error) {
      console.error('Failed to check new messages:', error)
      return []
    }
  }

  // 시간 포맷팅 유틸리티
  formatTime(timestamp) {
    if (!timestamp) return ''
    
    const now = new Date()
    const messageTime = new Date(timestamp)
    const diffDays = Math.floor((now - messageTime) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) {
      // 오늘: HH:MM
      return messageTime.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      })
    } else if (diffDays === 1) {
      // 어제
      return '어제'
    } else {
      // 그 전: MM/DD
      return messageTime.toLocaleDateString('ko-KR', { 
        month: '2-digit', 
        day: '2-digit' 
      })
    }
  }

  // 메시지 시간 포맷팅 (채팅 메시지용)
  formatMessageTime(timestamp) {
    if (!timestamp) return ''
    
    const messageTime = new Date(timestamp)
    return messageTime.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    })
  }
}

// 싱글톤 인스턴스
const antsTalkService = new AntsTalkService()

export default antsTalkService